import { useMemo } from 'react';

import { useBundleContent, useBundleProducts } from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { type MenuContentCategory, type MenuContentProduct } from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the bundle card prepended to the menu categories Contentful lists.
 * Returns nothing while the flag is off, the entry is loading or lists no
 * categories, or the restaurant has none of the bundle products in stock.
 */
export const useMenuBundleEntryPoint = (params: Params) => {
  const { categories } = params;

  const isBundlesEnabled = useFeatureFlag('CELS-5880-order-product-bundles-enabled');
  const { content, fetching } = useBundleContent();

  const bundleProducts = useBundleProducts({ categories, lineageKeys: content.lineageKeys });

  // ─── Derived ─────────────────────────────────────────────────────────

  const categoryIds = useMemo(
    () => new Set(content.entryPointCategoryIds),
    [content.entryPointCategoryIds],
  );

  const hasAvailableProducts = bundleProducts.some(checkIfProductAvailable);
  const isEntryPointVisible = isBundlesEnabled && categoryIds.size > 0 && hasAvailableProducts; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    if (!isEntryPointVisible) {
      return;
    }

    return { content, categoryIds };
  }, [categoryIds, content, isEntryPointVisible]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfProductAvailable(product: MenuContentProduct) {
  return !product.outOfStock;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  categories: readonly MenuContentCategory[];
};
